import React, { useMemo } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import DataTable from "react-data-table-component";
import moment from "moment";
import { longDateString } from "../../utils";
import "../../starek.css";

function MaintenanceSchedulesListingView({
    items,
    selectedRows,
    onSelectedRowsChange,
    toggleCleared,
    loading,
    noDataComponent,
    onRowClicked,
    onSort,
}) {
    // `items` is the current page of schedules (each with its `device`), searched and
    // sorted on the server; the page's Paginator handles paging, so the table's own
    // pager is off. Rows keep the shape the page's handlers read: scheduleId for the
    // route and the delete / toggle ids, `schedule` for its name and is_active.
    const rows = useMemo(
        () =>
            items.map((schedule) => {
                let attrs = schedule?.schedule_attributes;
                if (typeof attrs === "string") {
                    try {
                        attrs = JSON.parse(attrs);
                    } catch {
                        attrs = { when: attrs };
                    }
                }
                const device = schedule.device || { id: schedule.device_id };
                return {
                    id: `${device.id}-${schedule.id}`,
                    deviceId: device.id,
                    deviceName: device.name || device.id,
                    scheduleId: schedule.id,
                    scheduleType: schedule.schedule_type || "-",
                    from: attrs?.from || "-",
                    to: attrs?.to || "-",
                    when: attrs?.when || "-",
                    device,
                    schedule,
                };
            }),
        [items],
    );

    const columns = [
        {
            // Search matches the schedule's name / description, so show the name.
            name: "Name",
            selector: (r) => r.schedule?.name || "-",
            sortable: true,
            sortField: "name",
            height: "50px",
            grow: 1.5,
        },
        {
            name: "Device",
            selector: (r) => r.deviceName || "",
            sortable: true,
            sortField: "device",
            height: "50px",
            grow: 1.5,
            cell: (r) => (
                <div>
                    {r.deviceName || "-"}
                    {r.schedule?.is_active === false && (
                        <span className="badge bg-secondary ms-2">Inactive</span>
                    )}
                </div>
            ),
        },
        {
            name: "Type",
            selector: (r) => String(r.scheduleType || ""),
            sortable: true,
            sortField: "schedule_type",
            height: "50px",
            width: "150px",
        },
        {
            name: "From",
            selector: (r) => r.from || "",
            height: "50px",
            width: "180px",
            cell: (r) => {
                if (!r.from || r.from === "-") return "-";
                try {
                    return moment(r.from).format("lll");
                } catch {
                    return r.from;
                }
            },
        },
        {
            name: "To",
            selector: (r) => r.to || "",
            height: "50px",
            width: "180px",
            cell: (r) => {
                if (!r.to || r.to === "-") return "-";
                try {
                    return moment(r.to).format("lll");
                } catch {
                    return r.to;
                }
            },
        },
        {
            name: "When",
            selector: (r) => r.when || "",
            height: "50px",
            grow: 3,
            cell: (r) => (
                <div className="text-truncate h-50px" style={{ maxWidth: 250 }}>
                    {r.when || "-"}
                </div>
            ),
        },
        {
            name: "Activity",
            selector: (r) => r.schedule?.updated_at,
            sortable: true,
            sortField: "updated_at",
            height: "50px",
            width: "250px",
            cell: (r) => (
                <OverlayTrigger
                    placement="top"
                    overlay={
                        <Tooltip>
                            <div>
                                Created by{" "}
                                {r.schedule?.created_by?.name || "Unknown"} at{" "}
                                {r.schedule?.created_at
                                    ? longDateString(r.schedule.created_at)
                                    : "-"}
                            </div>
                            <div>
                                Modified by{" "}
                                {r.schedule?.updated_by?.name || "Unknown"} at{" "}
                                {r.schedule?.updated_at
                                    ? longDateString(r.schedule.updated_at)
                                    : "-"}
                            </div>
                        </Tooltip>
                    }
                >
                    <div style={{ fontSize: "0.85rem" }}>
                        {r.schedule?.updated_by?.name || "Unknown"} at{" "}
                        {r.schedule?.updated_at
                            ? longDateString(r.schedule.updated_at)
                            : "-"}
                    </div>
                </OverlayTrigger>
            ),
        },
    ];

    return (
        <DataTable
            columns={columns}
            data={rows}
            selectableRows
            onSelectedRowsChange={onSelectedRowsChange}
            clearSelectedRows={toggleCleared}
            highlightOnHover
            striped
            pointerOnHover
            onRowClicked={(row) => onRowClicked?.(row)}
            responsive
            dense
            sortServer
            onSort={(column, direction) =>
                onSort?.(column.sortField ? { field: column.sortField, direction } : null)
            }
            progressPending={!!loading}
            selectableRowsHighlight
            selectableRowsNoSelectAll={false}
            noDataComponent={noDataComponent}
            conditionalRowStyles={[
                {
                    when: (r) => r.schedule?.is_active === false,
                    style: { opacity: 0.5, backgroundColor: "#f8f9fa" },
                },
            ]}
        />
    );
}

export default MaintenanceSchedulesListingView;
